import { Button, Chip, Stack, Typography } from "@mui/material";
import React from "react";
import { ModalContext } from "../../../../Components/Modals";
import { GridRowsProp, GridColDef, GridRowParams, GridActionsCellItem } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { orderService, userService } from "../../../../Core/Api/services";
import { GroupAdvancedInfos } from "../../../../Core/Api/Types/group-advanced-infos";
import { DbOrderPriceRuleInfos } from "../../../../Core/Api/Types/db-order-price-rule-infos";
import ModalPriceRule, {
  ModalPriceRuleElementProps,
} from "../../../../Components/Modals/ModalPriceRule/modal-price-rule";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { useTranslation } from "react-i18next";
import { ContentCopyOutlined, FileCopyOutlined } from "@mui/icons-material";
import TableUuids from "../../../../Components/Table/table-uuids";

function AdminSettingsPriceRulesManagement() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [rows, setRows] = React.useState<DbOrderPriceRuleInfos[]>([]);
  const [groups, setGroups] = React.useState<GroupAdvancedInfos[]>([]);
  const [trigger, setTrigger] = React.useState<boolean>(false);
  const enqueueCustomSnackbar = useCustomSnackbar();

  // Modal
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  React.useEffect(() => {
    Promise.all([orderService.GetPriceRules(), userService.GetGroupsList()]).then(
      ([priceRulesResponse, groupsResponse]) => {
        setRows(priceRulesResponse.priceRules || []);
        setGroups(groupsResponse.groups || []);
        setIsLoading(false);
      }
    );
  }, [trigger]);

  // #region Grid definition
  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("common:name"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,

      renderCell: (data: Row) => (
        <Typography title={data.value} variant="body1">
          {data.value || "-"}
        </Typography>
      ),
    },
    {
      headerName: t("field:label.productTypeLowerCase"),
      field: "productType",
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: Row) => <Chip size="small" label={data.value} />,
    },
    //@TODO multiselect filter
    {
      field: "groupIds",
      headerName: t("common:groups"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      valueGetter: (data: Row, row: any) =>
        row?.groupIds?.map((x) => groups.find((y) => y.id === x)?.group?.name || "") || [],
      renderCell: (data: Row) =>
        data?.row?.groupIds && (
          <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
            {data?.row?.groupIds.map((x: number, index) => (
              <Chip label={groups.find((g) => g.id === x)?.group?.name || "-"} key={index} color="info" size="small" />
            ))}
          </Stack>
        ),
    },
    {
      field: "price",
      headerName: t("common:price"),
      type: "string",
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      valueGetter: (data: any, row: any) => row?.price?.map((x) => `${x.price || ""} ${x.currency || ""}`).join(","),
      renderCell: (data: Row) =>
        data?.row?.price ? (
          <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
            {data?.row?.price.map((x, index) => (
              <Chip label={x.price + " " + x.currency} key={index} color="secondary" size="small" />
            ))}
          </Stack>
        ) : (
          ""
        ),
    },

    {
      field: "ruleSet",
      headerName: t("table:headerName.ruleSet"),
      filterable: false,
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: Row) => (
        <Typography
          title={data.value}
          variant="body1"
          color="text.secondary"
          sx={{ wordWrap: "normal", wordBreak: "break-all" }}
        >
          {t("table:renderCell.countCondition", { count: data.value.length })}
        </Typography>
      ),
    },

    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<FileCopyOutlined sx={{ width: 20, height: 20, color: "text.secondary" }} />}
          label={t("button:addToCollection_one")}
          title={t("common:copy")}
          disabled={!UserHasAcl(AclTypes.aclOrderPriceRuleCreate)}
          onClick={() => {
            if (!openModal) return;

            openModal<ModalPriceRuleElementProps>(ModalPriceRule, {
              priceRule: params.row,
              mode: "CREATE",
            });
          }}
        />,
        <GridActionsCellItem
          icon={<EditIcon sx={{ width: 20, height: 20, color: "primary.text" }} />}
          label={t("common:share")}
          title={t("common:editUpperCase")}
          disabled={!UserHasAcl(AclTypes.aclOrderPriceRuleCreate)}
          onClick={() => {
            if (!openModal) return;

            openModal<ModalPriceRuleElementProps>(ModalPriceRule, {
              priceRule: params.row,
              mode: "UPDATE",
            });
          }}
        />,
        <GridActionsCellItem
          icon={<ContentCopyOutlined sx={{ width: 20, height: 20, color: "error.main" }} />}
          label={t("common:addToBasket")}
          title={t("common:delete")}
          disabled={!UserHasAcl(AclTypes.aclOrderPriceRuleDelete)}
          onClick={() => {
            if (!openModal) return;

            openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
              displayComment: true,
              commentLabel: t("modal:comment.confirmDeletePriceRule"),
              title: t("modal:title.deletePriceRule"),
              onConfirm: () => {
                orderService.DeletePriceRule(params?.row?.id).then(() => {
                  enqueueCustomSnackbar(t("alert:success.priceRuleDeleted"), "success");
                  setTrigger((prevState) => !prevState);
                });
              },
            });
          }}
        />,
      ],
    },
  ];
  // #endregion

  const additionalActions = (
    <Button
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      onClick={() => {
        if (!openModal) return;

        openModal<ModalPriceRuleElementProps>(ModalPriceRule, {
          priceRule: {
            $type: "VDM.AirLab.Sdk.Core.Database.Types.Order.DbOrderPriceRuleInfos, VDM.AirLab.Sdk.Core",
            name: "",
            computedPrice: [],
            computedSummary: "",
            groupIds: [],
            id: -500,
            platformId: 0,
            price: [],
            productType: null,
            ruleSet: [],
          } as unknown as DbOrderPriceRuleInfos,
          mode: "CREATE",
          onConfirm: () => {
            setTrigger((prevState) => !prevState);
          },
        });
      }}
    >
      {t("button:newPriceRule")}
    </Button>
  );

  return (
    <DataTable
      loading={isLoading}
      tableKey={TableUuids.SETTINGS_PRICE_RULES}
      columns={columns}
      rows={rows}
      additionalActions={additionalActions}
      pagination={true}
      disableRowSelectionOnClick
      getRowHeight={() => "auto"}
      onRowClick={(params) => {
        if (!openModal) return;

        openModal<ModalPriceRuleElementProps>(ModalPriceRule, {
          priceRule: params.row,
          mode: "UPDATE",
        });
      }}
    />
  );
}

export default AdminSettingsPriceRulesManagement;
